import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import UpgradeModal from "@/components/UpgradeModal";
import api from "@/lib/api";
import { Flower2, Loader2, ExternalLink, Crown } from "lucide-react";

/**
 * Maison Arrangements — curated REAL flower/edible/gourmet vendors, unlocked by any Credit
 * Pack. Clicking "View & Send" opens the vendor's actual live site in a new tab to complete a
 * real purchase. Business Team Plan accounts see Priority Fulfillment vendors surfaced first.
 */
const Arrangements = () => {
  const [items, setItems] = useState([]);
  const [businessPriority, setBusinessPriority] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    api.get("/arrangements")
      .then((r) => { setItems(r.data.items || []); setBusinessPriority(!!r.data.business_priority); setNote(r.data.note || ""); })
      .catch((e) => { if (e?.response?.status === 403) setGated(true); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppShell>
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm mb-4">
          <Flower2 className="w-4 h-4 gold-text" /> Maison Arrangements
        </div>
        <h1 className="font-serif text-4xl mb-1">Flowers &amp; Edibles</h1>
        <p className="text-muted-foreground">Real, trusted vendors — pair a card with a bouquet or gourmet gift.</p>
        {note && <p className="text-xs text-muted-foreground/70 mt-2">{note}</p>}
      </div>

      {loading ? (
        <div className="text-center py-24 text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading arrangements…</div>
      ) : gated ? (
        <div className="surface rounded-[2rem] p-16 text-center" data-testid="arrangements-locked">
          <Crown className="w-12 h-12 mx-auto mb-4 gold-text" />
          <h3 className="font-serif text-3xl mb-2">A premium privilege</h3>
          <p className="text-muted-foreground mb-6">Maison Arrangements — real flower &amp; gourmet vendors — unlocks permanently with any Credit Pack.</p>
          <button onClick={() => setGated("modal")} data-testid="arrangements-upgrade-cta"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-2xl hover:opacity-90"><Crown className="w-4 h-4" /> Buy a Credit Pack</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="arrangements-grid">
          {items.map((a) => (
            <div key={a.id} className="surface rounded-[1.5rem] p-6 flex flex-col" data-testid="arrangement-card">
              {businessPriority && a.royal_priority && (
                <span className="text-[10px] uppercase tracking-wider gold-text mb-2 flex items-center gap-1"><Crown className="w-3 h-3" /> Priority Fulfillment</span>
              )}
              <span className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{a.category} · {a.vendor}</span>
              <h3 className="font-serif text-xl mb-2">{a.name}</h3>
              <p className="text-sm text-muted-foreground flex-1 mb-4">{a.description}</p>
              <a href={a.external_url} target="_blank" rel="noopener noreferrer" data-testid="arrangement-view-link"
                className="flex items-center justify-center gap-2 text-sm bg-gradient-to-r from-secondary to-primary text-white rounded-2xl py-2.5 hover:opacity-90">
                View &amp; Send <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          ))}
        </div>
      )}

      <UpgradeModal open={gated === "modal"} onClose={() => setGated(true)} tier="credit_pack" feature="Maison Arrangements" />
    </AppShell>
  );
};

export default Arrangements;
